import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { SortDescriptor } from '@progress/kendo-data-query';
import _ from 'lodash';
import { forkJoin } from 'rxjs';
import { GridInstanceWithSortSettingsRemoteDto } from 'src/generated/signalR/grid-instance-with-sort-settings-remote-dto';
import { GridSortSettingRemoteDto } from 'src/generated/signalR/grid-sort-setting-remote-dto';
import { BrickWallService } from 'src/generated/swagger';
import { GridSortSettingsChanged, GridSortSettingsUpdatedByServer, PersistAllGridSortSettings, PersistGridSortSettings } from './grid-sort.actions';

export interface IBrickGridSortSettings {
   // all gridFilters for one single brick instance
   gridSortSettings: { [gridNameKey: string]: SortDescriptor };
}

export interface GridSortStateModel {
   // key is BrickInstanceId
   brickSortSettings: { [brickInstanceId: string]: IBrickGridSortSettings };
}

@State<GridSortStateModel>({
   name: 'brickBrickSort',
   defaults: {
      brickSortSettings: {},
   },
})
@Injectable()
export class GridSortState {
   constructor(private brickWallService: BrickWallService, private store: Store, private actions$: Actions) {}

   private static getSpecificSortOrDefaultInternal(state: GridSortStateModel, brickInstanceId: string, gridName: string): SortDescriptor {
      const brickSortSettings: IBrickGridSortSettings = state.brickSortSettings[brickInstanceId];
      const defaultSortSetting = { field: null, dir: null };
      if (!brickSortSettings) {
         return defaultSortSetting;
      }
      return brickSortSettings.gridSortSettings[gridName] ?? defaultSortSetting;
   }

   @Selector([GridSortState])
   public static getBrickSortConfiguration(state: GridSortStateModel): (brickInstanceId: string, gridName: string) => SortDescriptor {
      return (brickInstanceId: string, gridName: string) => {
         const result = GridSortState.getSpecificSortOrDefaultInternal(state, brickInstanceId, gridName);
         return _.isNil(result?.field) ? null : result;
      };
   }

   @Selector([GridSortState.getBrickSortConfiguration])
   public static getBrickSortConfigurationArray(
      getSortDesc: (brickInstanceId: string, gridName: string) => SortDescriptor
   ): (brickInstanceId: string, gridName: string) => SortDescriptor[] {
      return (brickInstanceId: string, gridName: string) => {
         const sortDesc = getSortDesc(brickInstanceId, gridName);
         const sortDescription: SortDescriptor[] = [];
         if (!_.isNil(sortDesc)) {
            sortDescription.push(sortDesc);
         }
         return sortDescription;
      };
   }

   private augmentState(state: Readonly<GridSortStateModel>, brickInstanceId: string, gridName: string) {
      if (_.isNil(state.brickSortSettings[brickInstanceId])) {
         state.brickSortSettings[brickInstanceId] = { gridSortSettings: {} };
      }
      if (_.isNil(state.brickSortSettings[brickInstanceId].gridSortSettings[gridName])) {
         state.brickSortSettings[brickInstanceId].gridSortSettings[gridName] = null;
      }
   }

   @Action(GridSortSettingsUpdatedByServer)
   @ImmutableContext()
   public gridSortSettingsUpdatedByServer(ctx: StateContext<GridSortStateModel>, action: GridSortSettingsUpdatedByServer) {
      ctx.setState((state) => {
         _.forEach(action.sortSettings.gridSortSettings, (brickGrids: GridInstanceWithSortSettingsRemoteDto, brickInstanceId) => {
            _(brickGrids.gridSortSettings).forEach((gridSortState: GridSortSettingRemoteDto, gridName) => {
               const sortSetting = GridSortState.getSpecificSortOrDefaultInternal(state, brickInstanceId, gridName);
               sortSetting.field = gridSortState?.field;
               sortSetting.dir = <'asc' | 'desc'>gridSortState?.direction;
               this.augmentState(state, brickInstanceId, gridName);
               state.brickSortSettings[brickInstanceId].gridSortSettings[gridName] = sortSetting;
            });
         });
         return state;
      });
   }

   @Action(GridSortSettingsChanged)
   @ImmutableContext()
   public gridFilterSettingsChanged(ctx: StateContext<GridSortStateModel>, action: GridSortSettingsChanged) {
      ctx.setState((state) => {
         this.augmentState(state, action.brickInstanceId, action.gridName);
         state.brickSortSettings[action.brickInstanceId].gridSortSettings[action.gridName] = action.customSorting;
         return state;
      });
      return this.store.dispatch(new PersistGridSortSettings(action.brickInstanceId));
   }

   @Action(PersistGridSortSettings)
   public persistGridSortSettings(ctx: StateContext<GridSortStateModel>, action: PersistGridSortSettings) {
      const state = ctx.getState();
      if (_.isNil(state.brickSortSettings[action.brickInstanceId])) {
         return;
      }
      const sortSettings: { [key: string]: GridSortSettingRemoteDto } = _.mapValues(
         state.brickSortSettings[action.brickInstanceId].gridSortSettings,
         (gridSortSettings) => {
            return <GridSortSettingRemoteDto>{
               field: gridSortSettings.field,
               direction: gridSortSettings.dir,
            };
         }
      );
      return this.brickWallService.brickGridSortSettings({
         brickInstanceId: action.brickInstanceId,
         gridSortSettings: sortSettings,
      });
   }

   @Action(PersistAllGridSortSettings)
   public PersistAllGridSortSettings(ctx: StateContext<GridSortStateModel>, action: PersistAllGridSortSettings) {
      const observables = action.brickInstanceIds.map((brickInstanceId) => ctx.dispatch(new PersistGridSortSettings(brickInstanceId)));
      if (_.isEmpty(observables)) {
         return;
      }

      return forkJoin(observables);
   }
}
